import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Logo from "../assets/RCS-Logo-White.png";
import CollapsedLogo from "../assets/RCS-Logo-White.png";
import { useDispatch, useSelector } from "react-redux";
import { logoutUser } from "../store/auth-slice";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { confirmAlert } from "react-confirm-alert";
import clsx from "clsx";
import supabase from "../supabaseClient";

const Sidebar = ({ forceExpanded = false }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [isLargeScreen, setIsLargeScreen] = useState(() => {
    if (typeof window === "undefined") return true;
    return Boolean(forceExpanded || window.innerWidth >= 1024);
  });
  const [textVisible, setTextVisible] = useState(() => {
    if (typeof window === "undefined") return true;
    return Boolean(forceExpanded || window.innerWidth >= 1024);
  });
  const [openOps, setOpenOps] = useState(true);
  const [openShipments, setOpenShipments] = useState(true);
  const [openBilling, setOpenBilling] = useState(true);
  const [openAccounting, setOpenAccounting] = useState(true);
  const [pendingRequestCount, setPendingRequestCount] = useState(0);
  const user = useSelector((state) => state.auth.user);
  const { first_name = '', last_name = '', department = '' } = user || {};
  const normalizedRole = String(user?.role || "").trim().toLowerCase();
  const isSuperadmin = normalizedRole === "superadmin";
  const isCostAnalysisApprover = Number(user?.user_id || user?.id || 0) === 127;

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleResize = () => {
      const width = window.innerWidth;
      const isLarge = Boolean(forceExpanded || width >= 1025);
      setIsLargeScreen(isLarge);
      setTextVisible(isLarge);
    };

    window.addEventListener("resize", handleResize);

    // Run it once when the component mounts
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, [forceExpanded]);

  useEffect(() => {
    const fetchPendingRequestCount = async () => {
      try {
        const { count, error } = await supabase
          .from("users")
          .select("user_id", { count: "exact", head: true })
          .eq("is_approved", false);

        if (error) throw error;

        setPendingRequestCount(count || 0);
      } catch (error) {
        console.error("Error fetching pending request count:", error);
      }
    };

    fetchPendingRequestCount();

    const channel = supabase
      .channel("pending-request-count")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "users" },
        () => {
          fetchPendingRequestCount();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const isActive = (path) =>
    location.pathname === path;

  const handleLogout = () => {
    dispatch(logoutUser()).then((data) => {
      const message = data?.payload?.message;
      if (data?.payload?.success) {
        toast.success(message, { position: "top-right" });
      } else {
        toast.error(message, { position: "top-right" });
      }
      sessionStorage.removeItem("passwordResetNotificationClosed");
    });
  };

  const handleLogoutConfirmation = () => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <div className="bg-white p-8 rounded-3xl shadow-2xl max-w-md mx-4 w-full">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <i className="fas fa-sign-out-alt text-white text-xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-blue mb-2">Confirm Logout</h2>
              <p className="text-grey">Are you sure you want to sign out of your account?</p>
            </div>
            <div className="flex gap-4">
              <button
                onClick={onClose}
                className="flex-1 px-6 py-3 border-2 border-gray-200 text-grey font-semibold rounded-xl hover:border-gray-300 hover:bg-gray-50 transition-all duration-300"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleLogout();
                  onClose();
                }}
                className="flex-1 px-6 py-3 bg-red text-white font-semibold rounded-xl hover:bg-red/90 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02]"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      ),
    });
  };

  return (
    <div className="flex font-poppins h-full">
      {/* Modern Sidebar */}
      <div
        className={clsx(
          "h-full bg-blue text-white transition-all duration-500 ease-out backdrop-blur-xl shadow-2xl border-r border-white/10",
          isLargeScreen ? "w-72" : "w-20"
        )}
        style={{ willChange: 'transform', transform: 'translateZ(0)' }}
      >
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-4 w-32 h-32 bg-yellow/5 rounded-full blur-2xl animate-pulse"></div>
          <div className="absolute bottom-32 left-4 w-24 h-24 bg-white/5 rounded-full blur-xl animate-pulse delay-1000"></div>
        </div>

        {/* Logo Section */}
        <div
          className={`relative flex items-center justify-center p-6 border-b border-white/10 backdrop-blur-sm ${
            isLargeScreen ? "h-28" : "h-20"
          }`}
        >
          <div className="relative">
            <img
              src={isLargeScreen ? Logo : CollapsedLogo}
              alt="Logo"
              className={`transition-all duration-500 ${
                isLargeScreen
                  ? "w-44 h-16 object-contain"
                  : "w-12 h-12 object-contain"
              }`}
            />
          </div>
        </div>


        {/* Navigation Menu */}
        <nav className="mt-8 px-4 space-y-2 overflow-y-auto pb-32" style={{ maxHeight: 'calc(100vh - 16rem)' }}>
          {/* Dashboard */}
          <Link
            to="/superAdmin/super-dashboard"
            className={`group relative flex items-center gap-4 px-5 py-4 rounded-2xl transition-all duration-300 transform hover:scale-[1.02] ${
              isLargeScreen ? "justify-start" : "justify-center"
            } ${
              isActive("/superAdmin/super-dashboard")
                ? "bg-gradient-to-r from-yellow to-yellow/90 text-blue shadow-lg shadow-yellow/25"
                : "text-white hover:bg-white/10 hover:shadow-lg"
            }`}
          >
            <div className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
              isActive("/superAdmin/super-dashboard") ? "text-blue" : "text-white hover:text-yellow"
            }`}>
              <i className="fa-solid fa-chart-line text-lg"></i>
              {isActive("/superAdmin/super-dashboard") && (
                <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
              )}
            </div>
            {textVisible && (
              <span className={`font-semibold transition-all duration-300 ${
                isActive("/superAdmin/super-dashboard")
                  ? "text-blue"
                  : "text-white group-hover:text-yellow"
              }`}>
                Dashboard
              </span>
            )}
          </Link>

          {/* Shipments */}
          <button
            type="button"
            onClick={() => setOpenShipments((v) => !v)}
            className="w-full mt-2 flex items-center gap-3 px-4 py-2 text-xs uppercase tracking-wide text-white/70 hover:text-white transition-colors"
          >
            <i className={`fa-solid fa-chevron-${openShipments ? "down" : "right"} text-[10px]`}></i>
            {textVisible && <span>Shipments</span>}
          </button>
          {openShipments && (
            <div className="space-y-2">
              <Link
                to="/superAdmin/create-shipment"
                className={`group relative flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 ${
                  isLargeScreen ? "justify-start" : "justify-center"
                } ${
                  isActive("/superAdmin/create-shipment")
                    ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                    : "text-white hover:bg-white/10 hover:shadow-lg"
                }`}
              >
                <div className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
                  isActive("/superAdmin/create-shipment") ? "text-blue" : "text-white group-hover:text-yellow"
                }`}>
                  <i className="fas fa-shipping-fast text-lg"></i>
                  {isActive("/superAdmin/create-shipment") && (
                    <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
                  )}
                </div>
                {textVisible && (
                  <span className={`font-semibold transition-all duration-300 ${
                    isActive("/superAdmin/create-shipment")
                      ? "text-blue"
                      : "text-white group-hover:text-yellow"
                  }`}>
                    Create Shipment
                  </span>
                )}
              </Link>

              <Link
                to="/superAdmin/shipment-management"
                className={`group relative flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 ${
                  isLargeScreen ? "justify-start" : "justify-center"
                } ${
                  isActive("/superAdmin/shipment-management")
                    ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                    : "text-white hover:bg-white/10 hover:shadow-lg"
                }`}
              >
                <div className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
                  isActive("/superAdmin/shipment-management") ? "text-blue" : "text-white group-hover:text-yellow"
                }`}>
                  <i className="fa-solid fa-dolly text-lg"></i>
                  {isActive("/superAdmin/shipment-management") && (
                    <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
                  )}
                </div>
                {textVisible && (
                  <span className={`font-semibold transition-all duration-300 ${
                    isActive("/superAdmin/shipment-management")
                      ? "text-blue"
                      : "text-white group-hover:text-yellow"
                  }`}>
                    Shipment Managements
                  </span>
                )}
              </Link>

          <Link
            to="/superAdmin/analytics"
            className={`group relative flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 ${
              isLargeScreen ? "justify-start" : "justify-center"
            } ${
                  isActive("/superAdmin/analytics")
                    ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                    : "text-white hover:bg-white/10 hover:shadow-lg"
                }`}
              >
                <div className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
                  isActive("/superAdmin/analytics") ? "text-blue" : "text-white group-hover:text-yellow"
                }`}>
                  <i className="fa-solid fa-chart-column text-lg"></i>
                  {isActive("/superAdmin/analytics") && (
                    <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
                  )}
                </div>
                {textVisible && (
                  <span className={`font-semibold transition-all duration-300 ${
                    isActive("/superAdmin/analytics")
                      ? "text-blue"
                      : "text-white group-hover:text-yellow"
                  }`}>
                    Analytics
                </span>
              )}
            </Link>


          </div>
        )}

          {/* Operations Management */}
          <button
            type="button"
            onClick={() => setOpenOps((v) => !v)}
            className="w-full mt-2 flex items-center gap-3 px-4 py-2 text-xs uppercase tracking-wide text-white/70 hover:text-white transition-colors"
          >
            <i className={`fa-solid fa-chevron-${openOps ? "down" : "right"} text-[10px]`}></i>
            {textVisible && <span>Operations Management</span>}
          </button>
          {openOps && (
            <>
              <Link
                to="/superAdmin/employee-directory"
                className={`group relative flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 ${
                  isLargeScreen ? "justify-start" : "justify-center"
                } ${
                  isActive("/superAdmin/employee-directory")
                    ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                    : "text-white hover:bg-white/10 hover:shadow-lg"
                }`}
              >
                <div className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
                  isActive("/superAdmin/employee-directory") ? "text-blue" : "text-white group-hover:text-yellow"
                }`}>
                  <i className="fa-solid fa-people-group text-lg"></i>
                  {isActive("/superAdmin/employee-directory") && (
                    <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
                  )}
                </div>
                {textVisible && (
                  <span className={`font-semibold transition-all duration-300 ${
                    isActive("/superAdmin/employee-directory")
                      ? "text-blue"
                      : "text-white group-hover:text-yellow"
                  }`}>
                    Employee Directory
                  </span>
                )}
              </Link>

              <Link
                to="/superAdmin/audit-logs"
                className={`group relative flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 ${
                  isLargeScreen ? "justify-start" : "justify-center"
                } ${
                  isActive("/superAdmin/audit-logs")
                    ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                    : "text-white hover:bg-white/10 hover:shadow-lg"
                }`}
              >
                <div className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
                  isActive("/superAdmin/audit-logs") ? "text-blue" : "text-white group-hover:text-yellow"
                }`}>
                  <i className="fa-solid fa-history text-lg"></i>
                  {isActive("/superAdmin/audit-logs") && (
                    <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
                  )}
                </div>
                {textVisible && (
                  <span className={`font-semibold transition-all duration-300 ${
                    isActive("/superAdmin/audit-logs")
                      ? "text-blue"
                      : "text-white group-hover:text-yellow"
                  }`}>
                    Audit Logs
                  </span>
                )}
              </Link>

              <Link
                to="/superAdmin/create-user"
                className={`group relative flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 ${
                  isLargeScreen ? "justify-start" : "justify-center"
                } ${
                  isActive("/superAdmin/create-user")
                    ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                    : "text-white hover:bg-white/10 hover:shadow-lg"
                }`}
              >
                <div className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
                  isActive("/superAdmin/create-user") ? "text-blue" : "text-white group-hover:text-yellow"
                }`}>
                  <i className="fa-regular fa-square-plus text-lg"></i>
                  {isActive("/superAdmin/create-user") && (
                    <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
                  )}
                </div>
                {textVisible && (
                  <span className={`font-semibold transition-all duration-300 ${
                    isActive("/superAdmin/create-user")
                      ? "text-blue"
                      : "text-white group-hover:text-yellow"
                  }`}>
                    User Management
                  </span>
                )}
              </Link>

              <Link
                to="/superAdmin/requestor-approval"
                className={`group relative flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 ${
                  isLargeScreen ? "justify-start" : "justify-center"
                } ${
                  isActive("/superAdmin/requestor-approval")
                    ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                    : "text-white hover:bg-white/10 hover:shadow-lg"
                }`}
                >
                  <div className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
                  isActive("/superAdmin/requestor-approval") ? "text-blue" : "text-white group-hover:text-yellow"
                }`}>
                  <i className="fa-solid fa-user-check text-lg"></i>
                  {isActive("/superAdmin/requestor-approval") && (
                    <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
                  )}
                  {pendingRequestCount > 0 && (
                    <span className="absolute -top-2 -right-2 min-w-5 h-5 px-1 rounded-full bg-yellow text-blue text-[10px] font-bold leading-5 text-center shadow-md">
                      {pendingRequestCount > 99 ? "99+" : pendingRequestCount}
                    </span>
                  )}
                </div>
                {textVisible && (
                  <div className="flex items-center gap-2">
                    <span className={`font-semibold transition-all duration-300 ${
                      isActive("/superAdmin/requestor-approval")
                        ? "text-blue"
                        : "text-white group-hover:text-yellow"
                    }`}>
                      Requestor Approval
                    </span>
                    {pendingRequestCount > 0 && (
                      <span className="inline-flex min-w-6 h-6 items-center justify-center rounded-full bg-yellow px-2 text-xs font-bold text-blue shadow-md">
                        {pendingRequestCount > 99 ? "99+" : pendingRequestCount}
                      </span>
                    )}
                  </div>
                )}
              </Link>

              <Link
                to="/superAdmin/department-management"
                className={`group relative flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 ${
                  isLargeScreen ? "justify-start" : "justify-center"
                } ${
                  isActive("/superAdmin/department-management")
                    ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                    : "text-white hover:bg-white/10 hover:shadow-lg"
                }`}
              >
                <div className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
                  isActive("/superAdmin/department-management") ? "text-blue" : "text-white group-hover:text-yellow"
                }`}>
                  <i className="fa-solid fa-building text-lg"></i>
                  {isActive("/superAdmin/department-management") && (
                    <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
                  )}
                </div>
                {textVisible && (
                  <span className={`font-semibold transition-all duration-300 ${
                    isActive("/superAdmin/department-management")
                      ? "text-blue"
                      : "text-white group-hover:text-yellow"
                  }`}>
                    Department Management
                  </span>
                )}
              </Link>
            </>
          )}

          {/* Billing */}
          <button
            type="button"
            onClick={() => setOpenBilling((v) => !v)}
            className="w-full mt-2 flex items-center gap-3 px-4 py-2 text-xs uppercase tracking-wide text-white/70 hover:text-white transition-colors"
          >
            <i className={`fa-solid fa-chevron-${openBilling ? "down" : "right"} text-[10px]`}></i>
            {textVisible && <span>Billing</span>}
          </button>
          {openBilling && (
            <>
              {(isSuperadmin || department?.trim()?.toLowerCase() === "accounting") && (
                <Link
                  to="/superAdmin/billing-upload"
                  className={`group relative flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 ${
                    isLargeScreen ? "justify-start" : "justify-center"
                  } ${
                    isActive("/superAdmin/billing-upload")
                      ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                      : "text-white hover:bg-white/10 hover:shadow-lg"
                  }`}
                >
                  <div className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
                    isActive("/superAdmin/billing-upload") ? "text-blue" : "text-white group-hover:text-yellow"
                  }`}>
                    <i className="fa-solid fa-file-invoice-dollar text-lg"></i>
                    {isActive("/superAdmin/billing-upload") && (
                      <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
                    )}
                  </div>
                  {textVisible && (
                    <span className={`font-semibold transition-all duration-300 ${
                      isActive("/superAdmin/billing-upload")
                        ? "text-blue"
                        : "text-white group-hover:text-yellow"
                    }`}>
                      Billing Upload
                    </span>
                  )}
                </Link>
              )}

              <Link
                to="/superAdmin/billing-list"
                className={`group relative flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 ${
                  isLargeScreen ? "justify-start" : "justify-center"
                } ${
                  isActive("/superAdmin/billing-list")
                    ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                    : "text-white hover:bg-white/10 hover:shadow-lg"
                }`}
              >
                <div className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
                  isActive("/superAdmin/billing-list") ? "text-blue" : "text-white group-hover:text-yellow"
                }`}>
                  <i className="fa-solid fa-file-invoice text-lg"></i>
                  {isActive("/superAdmin/billing-list") && (
                    <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
                  )}
                </div>
                {textVisible && (
                  <span className={`font-semibold transition-all duration-300 ${
                    isActive("/superAdmin/billing-list")
                      ? "text-blue"
                      : "text-white group-hover:text-yellow"
                  }`}>
                    Billing List
                  </span>
                )}
              </Link>

              <Link
                to="/superAdmin/billed-shipments"
                className={`group relative flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 ${
                  isLargeScreen ? "justify-start" : "justify-center"
                } ${
                  isActive("/superAdmin/billed-shipments")
                    ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                    : "text-white hover:bg-white/10 hover:shadow-lg"
                }`}
              >
                <div className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
                  isActive("/superAdmin/billed-shipments") ? "text-blue" : "text-white group-hover:text-yellow"
                }`}>
                  <i className="fa-solid fa-file-invoice-dollar text-lg"></i>
                  {isActive("/superAdmin/billed-shipments") && (
                    <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
                  )}
                </div>
                {textVisible && (
                  <span className={`font-semibold transition-all duration-300 ${
                    isActive("/superAdmin/billed-shipments")
                      ? "text-blue"
                      : "text-white group-hover:text-yellow"
                  }`}>
                    Billed Shipment
                  </span>
                )}
              </Link>

              <Link
                to="/superAdmin/document-templates"
                className={`group relative flex items-center gap-4 px-4 py-4 rounded-2xl transition-all duration-300 transform hover:scale-[1.02] ${
                  isLargeScreen ? "justify-start" : "justify-center"
                } ${
                  isActive("/superAdmin/document-templates")
                    ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                    : "text-white hover:bg-white/10 hover:shadow-lg"
                }`}
                style={{ animationDelay: "900ms" }}
              >
                <div className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
                  isActive("/superAdmin/document-templates") ? "text-blue" : "text-white group-hover:text-yellow"
                }`}>
                  <i className="fa-solid fa-file-lines text-lg"></i>
                  {isActive("/superAdmin/document-templates") && (
                    <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
                  )}
                </div>
                {textVisible && (
                  <span className={`font-semibold transition-all duration-300 ${
                    isActive("/superAdmin/document-templates")
                      ? "text-blue"
                      : "text-white group-hover:text-yellow"
                  }`}>
                    Document Templates
                  </span>
                )}
              </Link>
            </>
          )}


          {/* Accounting */}
          {(isSuperadmin || department?.toLowerCase() === "accounting") && (
            <>
              <button
                type="button"
                onClick={() => setOpenAccounting((v) => !v)}
                className="w-full mt-2 flex items-center gap-3 px-4 py-2 text-xs uppercase tracking-wide text-white/70 hover:text-white transition-colors"
              >
                <i className={`fa-solid fa-chevron-${openAccounting ? "down" : "right"} text-[10px]`}></i>
                {textVisible && <span>Accounting</span>}
              </button>
              {openAccounting && (
                <div className="space-y-2">
                  <Link
                    to="/accounting"
                    className={`group relative flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 ${
                      isLargeScreen ? "justify-start" : "justify-center"
                    } ${
                      isActive("/accounting")
                        ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                        : "text-white hover:bg-white/10 hover:shadow-lg"
                    }`}
                  >
                    <div
                      className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
                        isActive("/accounting")
                          ? "text-blue"
                          : "text-white group-hover:text-yellow"
                      }`}
                    >
                      <i className="fa-solid fa-file-pen text-lg"></i>
                      {isActive("/accounting") && (
                        <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
                      )}
                    </div>
                    {textVisible && (
                      <span
                        className={`font-semibold transition-all duration-300 ${
                          isActive("/accounting")
                            ? "text-blue"
                            : "text-white group-hover:text-yellow"
                        }`}
                      >
                        Accounting
                      </span>
                    )}
                  </Link>

                  <Link
                    to="/superAdmin/jo-requests"
                    className={`group relative flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 ${
                      isLargeScreen ? "justify-start" : "justify-center"
                    } ${
                      isActive("/superAdmin/jo-requests")
                        ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                        : "text-white hover:bg-white/10 hover:shadow-lg"
                    }`}
                  >
                    <div
                      className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
                        isActive("/superAdmin/jo-requests")
                          ? "text-blue"
                          : "text-white group-hover:text-yellow"
                      }`}
                    >
                      <i className="fa-solid fa-diagram-project text-lg"></i>
                      {isActive("/superAdmin/jo-requests") && (
                        <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
                      )}
                    </div>
                    {textVisible && (
                      <span
                        className={`font-semibold transition-all duration-300 ${
                          isActive("/superAdmin/jo-requests")
                            ? "text-blue"
                            : "text-white group-hover:text-yellow"
                        }`}
                      >
                        JO Requests
                      </span>
                    )}
                  </Link>

                  <Link
                    to="/accounting/production-report"
                    className={`group relative flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 ${
                      isLargeScreen ? "justify-start" : "justify-center"
                    } ${
                      isActive("/accounting/production-report")
                        ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                        : "text-white hover:bg-white/10 hover:shadow-lg"
                    }`}
                  >
                    <div
                      className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
                        isActive("/accounting/production-report")
                          ? "text-blue"
                          : "text-white group-hover:text-yellow"
                      }`}
                    >
                      <i className="fa-solid fa-industry text-lg"></i>
                      {isActive("/accounting/production-report") && (
                        <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
                      )}
                    </div>
                    {textVisible && (
                      <span
                        className={`font-semibold transition-all duration-300 ${
                          isActive("/accounting/production-report")
                            ? "text-blue"
                            : "text-white group-hover:text-yellow"
                        }`}
                      >
                        Production Report
                      </span>
                    )}
                  </Link>

                  {isCostAnalysisApprover ? (
                    <Link
                      to="/superAdmin/cost-analysis-user127-approvals"
                      className={`group relative flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 ${
                        isLargeScreen ? "justify-start" : "justify-center"
                      } ${
                        isActive("/superAdmin/cost-analysis-user127-approvals")
                          ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                          : "text-white hover:bg-white/10 hover:shadow-lg"
                      }`}
                    >
                      <div
                        className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
                          isActive("/superAdmin/cost-analysis-user127-approvals")
                            ? "text-blue"
                            : "text-white group-hover:text-yellow"
                        }`}
                      >
                        <i className="fa-solid fa-circle-check text-lg"></i>
                        {isActive("/superAdmin/cost-analysis-user127-approvals") && (
                          <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
                        )}
                      </div>
                      {textVisible && (
                        <span
                          className={`font-semibold transition-all duration-300 ${
                            isActive("/superAdmin/cost-analysis-user127-approvals")
                              ? "text-blue"
                              : "text-white group-hover:text-yellow"
                          }`}
                        >
                          Cost Analysis Approval
                        </span>
                      )}
                    </Link>
                  ) : null}
                </div>
              )}
            </>
          )}
        </nav>

        {/* Enhanced Profile Section */}
        <div className="absolute bottom-0 left-0 w-full border-t border-white/10 backdrop-blur-sm">
          <div className="p-6 space-y-4">
            {/* Profile Info clickable */}
            <Link
              to={location.pathname.startsWith("/superAdmin") ? "/superAdmin/profile" : "/admin/profile"}
              className={`flex items-center gap-4 ${isLargeScreen ? "justify-start" : "justify-center"} hover:bg-white/10 rounded-2xl px-2 py-1 transition-colors`}
            >
              {/* Enhanced Avatar */}
              <div className="relative">
                <div className="bg-yellow text-blue font-bold rounded-2xl w-12 h-12 flex items-center justify-center shadow-lg">
                  {`${first_name?.[0] || ""}${last_name?.[0] || ""}`}
                </div>
              </div>

              {textVisible && (
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-white text-sm truncate">
                    {`${first_name || ""} ${last_name || ""}`}
                  </p>
                  <p className="text-xs text-white/70 truncate">View Profile</p>
                </div>
              )}
            </Link>

            <button
                          className={clsx(
                            "group relative flex items-center gap-3 w-full px-4 py-3",
                            "bg-red hover:bg-red/90",
                            "text-white rounded-2xl transition-all duration-300 transform hover:scale-[1.02] shadow-lg hover:shadow-xl",
                            isLargeScreen ? "justify-start" : "justify-center"
                          )}
                          onClick={handleLogoutConfirmation}
                        >
                          {/* Content above the overlay */}
                          <div className="relative z-10 flex items-center gap-3">
                            <div className="flex items-center justify-center w-5 h-5">
                              <i className="fa-solid fa-right-from-bracket text-sm group-hover:text-yellow transition-colors duration-300"></i>
                            </div>
            
                            {textVisible && (
                              <span className="font-semibold text-sm group-hover:text-yellow transition-colors duration-300">
                                Logout
                              </span>
                            )}
                          </div>
            
                          {/* Glow overlay lives under the text and ignores pointer events */}
                          <div className="pointer-events-none absolute inset-0 z-0 rounded-2xl bg-red/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm"></div>
                        </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
